import { QueryClient } from '@tanstack/react-query'
import { getAiToolkit } from '@tiptap-pro/ai-toolkit'
import { workspaceCategoryTypesQueryHelper } from '@opallabs/admin-configuration/src/CategoryTypeAdminView/queries/queryCache'
import { globalQueryKeys } from '@opallabs/api'
import { readBlock, readPlan } from '@opallabs/api/BFF'
import {
  boardObjectsIdentifiers,
  readBoard,
  readBoardObjects,
} from '@opallabs/api/v3'
import { queryKeys as boardsQueryKeys } from '@opallabs/boards'
import { boardListQueryKey } from '@opallabs/boards/src/queries'
import { getBackboneWindow } from '@opallabs/brown-dwarf'
import { MessageKeys } from '@opallabs/hooks'
import { planTypesQueryHelper } from '@opallabs/plans/src/queries/categoryTypes/queryCache'
import {
  createReviewDecorations,
  type RegisteredTiptapEditor,
} from '@opallabs/text-editor'
import { ThreadState } from './copilotThreadStateStore'
import {
  CreateBlocksToolCallResult,
  CreateBoardToolCallResult,
  CreateCollectionsToolCallResult,
  CreateConnectionsToolCallResult,
  CreateContentsToolCallResult,
  CreateKeyDatesToolCallResult,
  CreateMomentsToolCallResult,
  CreatePlanToolCallResult,
  CreateSmartSwimlaneToolCallResult,
  CreateSwimlanesToolCallResult,
  DisconnectBlocksToolCallResult,
  EntityContext,
  Message,
  ProposeToolCallResult,
  ProposedResource,
  ProposedBlock,
  ProposedBlockUpdate,
  ProposedBoardUpdate,
  ProposedCollectionUpdate,
  ProposedConnection,
  ProposedConnectionRemove,
  ProposedMoment,
  ProposedSwimlane,
  SetCustomFieldValuesToolCallResult,
  SetMomentCustomFieldValuesToolCallResult,
  SetNotesToolCallResult,
  StagedTiptapEdit,
  TextEditorDocument,
  TiptapEditToolCallResult,
  ToolCallResultEvent,
  PlaceMomentsToolCallResult,
  ProposedMomentPlacement,
  UpdateBlockToolCallResult,
  UpdateBoardObjectToolCallResult,
  UpdateBoardToolCallResult,
  UpdateCollectionToolCallResult,
  UpdateContentToolCallResult,
  UpdateKeyDatesToolCallResult,
  UpdateMomentToolCallResult,
  UpdatePlanToolCallResult,
} from './types'

// Find a proposal row by its proposal_id, which every executor result echoes.
// The ids are uuids minted by one propose call, so the id alone finds the row.
const findPendingProposalById = (
  messages: Array<Message>,
  proposalId: string | undefined
) => {
  if (!proposalId) return undefined
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'proposal' && m.state === 'pending') {
      const match = m.resources.find((r) => r.proposal_id === proposalId)
      if (match) return match
    }
  }
  return undefined
}

// Deploy skew: the web bundle and opal_ai ship separately, so an opal_ai from
// before the batch tool still answers with its one row flat, carrying no
// `results` array to read. Take it as the one row it describes. The tool's
// error boundary answers flat too, naming no row, which refreshes nothing.
const batchRows = <Row extends { proposal_id?: string }>(
  result: { results: Array<Row & { proposal_id: string }> } | Row
): Array<Row & { proposal_id: string }> => {
  if ('results' in result) return result.results
  const { proposal_id: proposalId } = result
  return proposalId ? [{ ...result, proposal_id: proposalId }] : []
}

/** Whether a result is a propose tool's, and so may carry a card. */
export const isProposeResult = (
  event: ToolCallResultEvent
): event is ProposeToolCallResult => event.tool_name.startsWith('propose_')

/**
 * The proposal rows a propose tool's result carries, or `undefined` when it
 * carries none.
 *
 * A propose tool can answer with a question instead of a proposal:
 * `propose_presentation` asks which brand theme to use when the workspace has
 * several, because generation can't tell which was meant and won't guess. The
 * caller dispatches inside the turn's Completed handler, so reading through an
 * absent list throws before the turn is marked complete and strands it
 * mid-stream.
 */
export const proposalsFromResult = (
  event: ProposeToolCallResult
): Array<ProposedResource> | undefined => {
  const { proposals } = event.result
  return Array.isArray(proposals) ? proposals : undefined
}

/**
 * Resolves which editor a `tiptap_edit` result targets. Gem names the
 * document by id. When it doesn't (and only one editor is mounted) that lone
 * editor is the target.
 */
export const resolveEditTarget = (
  editors: RegisteredTiptapEditor[],
  documentId: string | undefined
): RegisteredTiptapEditor | null => {
  const match = editors.find((entry) => entry.documentId === documentId)
  if (match) return match
  return editors.length === 1 ? editors[0] : null
}

/**
 * Stages a `tiptap_edit` tool-call result as a preview suggestion in the given
 * editor. The caller resolves the target via `resolveEditTarget` (which falls
 * back to the lone open editor when the id doesn't match) — so this takes the
 * resolved editor directly rather than re-finding by exact id, which previously
 * dropped the edit whenever Gem's `document_id` didn't match the open editor's.
 */
export const handleTiptapEditResult = (
  target: RegisteredTiptapEditor,
  result: TiptapEditToolCallResult['result']
) => {
  // A soft error (the backend refused the payload and told the model to
  // re-emit) carries no operations — there is nothing to stage, and feeding
  // it to the toolkit just logs a spurious validation error.
  if (!('operations' in result)) return
  // Don't stage edits into a read-only document
  if (!target.editor.isEditable) return

  const toolkit = getAiToolkit(target.editor)
  // The toolkit rejects `tiptapEdit` unless `tiptapRead` ran first; we
  // discard the result. Its schema requires `from`.
  const readResult = toolkit.executeTool({
    toolName: 'tiptapRead',
    input: { from: 0 },
  })
  if (readResult.hasError) {
    console.warn('[GemCopilot] tiptapRead failed', readResult)
  }

  const execResult = toolkit.executeTool({
    toolName: 'tiptapEdit',
    input: { operations: result.operations },
    reviewOptions: {
      // 'preview' mode holds edits as a suggestion until accepted/rejected
      mode: 'preview',
      useDiffUtility: true,
      diffUtilityOptions: { groupInlineChanges: 0.5 },
      displayOptions: {
        renderDecorations: createReviewDecorations({
          editor: target.editor,
          documentId: target.documentId,
          toolkit,
        }),
      },
    },
  })

  if (execResult.hasError) {
    console.warn('[GemCopilot] tiptapEdit rejected by toolkit', execResult)
  }
}

/**
 * Stages a kept `tiptap_edit` again in an editor that lost it: one that mounted
 * again, or one the user switched back to editing. An editor that still shows
 * Gem's suggestions keeps them, so this never stages a second copy.
 */
export const restageTiptapEdit = (
  target: RegisteredTiptapEditor,
  result: StagedTiptapEdit
) => {
  if (getAiToolkit(target.editor).getSuggestions().length > 0) return
  handleTiptapEditResult(target, result)
}

/**
 * Forwards a `tiptap_edit` result to the content text editor in the legacy
 * iframe (used when no Asgard editor is registered to handle it directly).
 * `restage` asks the iframe to stage a kept edit again (see `restageTiptapEdit`).
 */
export const forwardTiptapEditToIframe = (
  result: TiptapEditToolCallResult['result'],
  { restage = false }: { restage?: boolean } = {}
) => {
  // Same guard as handleTiptapEditResult: a soft error has no operations.
  if (!('operations' in result)) return
  getBackboneWindow()?.postMessage(
    { message: MessageKeys.tiptapEditResult, result, restage },
    '*'
  )
}

/**
 * Routes a `tiptap_edit` to whichever editor can show it, and reports whether
 * one took it.
 *
 * The iframe leg is a fire-and-forget `postMessage`, so the only evidence that
 * anything down there will stage it is `iframeDocuments`: the sole listener is
 * `GemContentEditorBridge`, which publishes the editors it holds back up under
 * that name. With no editor either side the write is lost while the model goes
 * on believing it wrote the document, so the caller has to say so.
 */
export const deliverTiptapEdit = (
  editors: RegisteredTiptapEditor[],
  iframeDocuments: Array<TextEditorDocument>,
  result: StagedTiptapEdit
): boolean => {
  const target = resolveEditTarget(editors, result.document_id)
  if (target) {
    handleTiptapEditResult(target, result)
    return true
  }

  if (iframeDocuments.length > 0) {
    forwardTiptapEditToIframe(result)
    return true
  }

  return false
}

/**
 * Tells the legacy iframe that a post's copy was updated server-side so the
 * open post can refresh in place (the post lives in the monolith iframe, which
 * doesn't otherwise know about the change).
 */
export const forwardContentUpdateToIframe = (
  result: UpdateContentToolCallResult['result']
) => {
  const rows = 'results' in result ? result.results : [result]

  rows.forEach((row) => {
    if (row.result !== 'updated' || !row.content_id) return

    getBackboneWindow()?.postMessage(
      { message: MessageKeys.contentUpdated, content_id: row.content_id },
      '*'
    )
  })
}

/**
 * Tells the legacy iframe that content was created server-side so the open
 * moment can rerender its posts list in place (the moment lives in the monolith
 * iframe, which doesn't otherwise know about the new posts). The batch creates
 * every post on the same viewed moment, so each created item is forwarded with
 * the shared `moment_id`.
 */
export const forwardContentCreateToIframe = (
  result: CreateContentsToolCallResult['result']
) => {
  // Posts can target different moments; forward each with its own moment_id. The
  // iframe rerenders only the moment it's currently showing and ignores the rest.
  result.results
    .filter((r) => r.result === 'created' && r.content_id && r.moment_id)
    .forEach((r) => {
      getBackboneWindow()?.postMessage(
        {
          message: MessageKeys.contentCreated,
          content_id: r.content_id,
          moment_id: r.moment_id,
        },
        '*'
      )
    })
}

// Refreshes whatever view the user is currently looking at after a moment is
// created or changed, so it reflects without a reload. The view depends on the
// entity context: a board reads moments through its board-objects query; the
// calendar (React Week + Month views) reads them off the workspace boards +
// moments queries (the mechanism the proto branch used); a single moment view
// reads the moment directly. `momentId` is supplied when the changed moment is
// known so its own cache can be busted too.
export const invalidateMomentInContext = (
  queryClient: QueryClient,
  workspaceId: string,
  entityContext: EntityContext,
  momentId?: string
) => {
  switch (entityContext.entity_type) {
    case 'board':
      readBoardObjects.invalidateCache(
        queryClient,
        boardObjectsIdentifiers.board({
          workspaceUuid: workspaceId,
          boardId: entityContext.entity_id,
        })
      )
      if (momentId) {
        queryClient.invalidateQueries(globalQueryKeys.momentById(momentId))
      }
      break
    case 'calendar':
      queryClient.invalidateQueries(
        globalQueryKeys.momentsByWorkspace(workspaceId)
      )
      // The continuum calendar (week/month) is Redux-backed inside the monolith
      // iframe, so query invalidation doesn't refresh it — tell it to refetch.
      getBackboneWindow()?.postMessage(
        { message: MessageKeys.refreshMoments },
        '*'
      )
      break
    case 'moment':
    case 'content':
      if (momentId) {
        queryClient.invalidateQueries(globalQueryKeys.momentById(momentId))
      }
      break
  }
}

/**
 * Refreshes the view the user is on once the batch `create_contents` result
 * lands, so the new posts appear without a reload. The board's Content column and the calendar's content icons read
 * `moment.channels` off the boardObjects / workspace-moments queries, which Gem
 * shares in the same window — so the view stays stale until those are busted.
 * The posts also live in the monolith iframe (refreshed via
 * forwardContentCreateToIframe), which the calendar branch still leans on.
 *
 * Per-moment `momentById` is intentionally not busted here: `create_contents`
 * echoes each post's LEGACY moment id, while `momentById` keys on the v3 UUID
 * (see the note near CreateContentsToolCallResult in types.ts). The ids don't
 * match, so the context-level invalidation covers the moment instead.
 */
export const handleCreateContents = (
  queryClient: QueryClient,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  if (workspaceId) {
    invalidateMomentInContext(queryClient, workspaceId, entityContext)
  }
}

export function handleCreateBoard(
  payload: CreateBoardToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined,
  navigate: (to: string) => void,
  workspaceSlug: string | undefined
) {
  if (workspaceId) {
    const boardListKey = boardListQueryKey(workspaceId, false)
    queryClient.invalidateQueries(boardListKey)
  }

  // Drop the user into the new board so onboarding continues in-space, mirroring
  // create_plan.
  if (
    workspaceSlug &&
    payload.result.result === 'created' &&
    payload.result.id
  ) {
    navigate(`/moments/${workspaceSlug}/boards/${payload.result.id}`)
  }
}

// Every view a batch executor landed items in. `view_ids` covers a batch that
// spanned several plans or sub-plans; `view_id` alone is the single-destination
// case (and older payloads that predate `view_ids`).
const affectedViewIds = (result: {
  view_id?: string
  view_ids?: Array<string>
}) =>
  Array.from(
    new Set(
      result.view_ids?.length
        ? result.view_ids
        : [result.view_id].filter((id): id is string => Boolean(id))
    )
  )

const invalidateViews = (
  queryClient: QueryClient,
  workspaceId: string,
  viewIds: Array<string>
) => {
  viewIds.forEach((viewId) => {
    queryClient.invalidateQueries(
      readPlan.queryKey({
        workspaceUuid: workspaceId,
        resourceId: viewId,
        queryParams: null,
      })
    )
  })
}

// The plan the user is on draws the parent block, not the sub-plan inside it,
// so a batch that landed items in a sub-plan leaves that plan stale: the block
// gains a sub-plan link, and its bar follows the work now inside it. An
// executor names only the views it wrote to, so refresh the plan in context
// as well.
const invalidatePlanInContext = (
  queryClient: QueryClient,
  workspaceId: string,
  entityContext: EntityContext
) => {
  if (entityContext.entity_type !== 'plan') return

  queryClient.invalidateQueries(
    readPlan.queryKey({
      workspaceUuid: workspaceId,
      resourceId: entityContext.entity_id,
      queryParams: null,
    })
  )
}

// Batch executor for all confirmed blocks. It refreshes every view the batch
// landed blocks in (a batch can span several plans and sub-plans); for blocks
// created inside another block it also refreshes that parent block (so its newly
// created sub-plan replaces "No Plan Added Yet") and the plan the user is on
// (which draws that block). Either way the workspace blocks list is refreshed.
export const handleCreateBlocks = (
  payload: CreateBlocksToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  if (workspaceId) {
    invalidateViews(queryClient, workspaceId, affectedViewIds(payload.result))

    // A batch can span several parent blocks (e.g. tactics across many
    // campaigns' sub-plans), so refresh every distinct parent block it touched.
    const parentBlockIds = new Set(
      payload.result.results
        .map(
          (result) =>
            (
              findPendingProposalById(store.messages, result.proposal_id) as
                | ProposedBlock
                | undefined
            )?.relationships.parent_block_id
        )
        .filter((id): id is string => Boolean(id))
    )
    parentBlockIds.forEach((parentBlockId) => {
      queryClient.invalidateQueries(
        readBlock.queryKey(
          readBlock.queryIdentifiers(workspaceId, parentBlockId)
        )
      )
    })

    if (parentBlockIds.size > 0) {
      invalidatePlanInContext(queryClient, workspaceId, entityContext)
    }

    queryClient.invalidateQueries(
      globalQueryKeys.blocksByWorkspace(workspaceId)
    )
  }
}

// Batch executor for all confirmed moments. Each created moment may sit on a
// different board, so refresh the workspace moments once and each created
// moment's board collection (read from its stored proposal).
export const handleCreateMoments = (
  payload: CreateMomentsToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  if (workspaceId) {
    queryClient.invalidateQueries(
      globalQueryKeys.momentsByWorkspace(workspaceId)
    )

    payload.result.results
      .filter((r) => r.result === 'created')
      .forEach((r) => {
        const proposal = findPendingProposalById(
          store.messages,
          r.proposal_id
        ) as ProposedMoment | undefined

        if (proposal?.relationships.board_id) {
          readBoardObjects.invalidateCache(
            queryClient,
            boardObjectsIdentifiers.collection({
              workspaceUuid: workspaceId,
              boardId: proposal.relationships.board_id,
              collectionId:
                proposal.relationships.board_object_collection_id ?? null,
            })
          )
        }

        // Refresh the view the user is on (e.g. calendar week/month) for the
        // created moment.
        invalidateMomentInContext(queryClient, workspaceId, entityContext, r.id)
      })
  }
}

// Batch executor for all confirmed placements. The moments already existed, so
// only their board placement changed: refresh each destination collection and
// the view the user is on.
export const handlePlaceMoments = (
  payload: PlaceMomentsToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  if (workspaceId) {
    payload.result.results
      .filter((r) => r.result === 'placed')
      .forEach((r) => {
        const proposal = findPendingProposalById(
          store.messages,
          r.proposal_id
        ) as ProposedMomentPlacement | undefined

        if (proposal) {
          readBoardObjects.invalidateCache(
            queryClient,
            boardObjectsIdentifiers.collection({
              workspaceUuid: workspaceId,
              boardId: proposal.relationships.board_id,
              collectionId:
                proposal.relationships.board_object_collection_id ?? null,
            })
          )
        }

        invalidateMomentInContext(queryClient, workspaceId, entityContext, r.id)
      })
  }
}

// Batch executor for every confirmed block update on a card. A batch can span
// several plans, so refresh each updated block and each plan it sits on.
export const handleUpdateBlock = (
  payload: UpdateBlockToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined
) => {
  // Deploy skew: the web bundle and opal_ai ship separately, so an opal_ai from
  // before the batch tool still answers with its one row flat, carrying no
  // `results` array to read. Take it as the one row it describes.
  const results =
    'results' in payload.result
      ? payload.result.results
      : [{ ...payload.result, proposal_id: payload.result.proposal_id ?? '' }]

  if (workspaceId) {
    const proposals = results
      .map(
        (r) =>
          findPendingProposalById(store.messages, r.proposal_id) as
            | ProposedBlockUpdate
            | undefined
      )
      .filter((p): p is ProposedBlockUpdate => Boolean(p))

    new Set(proposals.map((p) => p.view_id)).forEach((viewId) =>
      queryClient.invalidateQueries(
        readPlan.queryKey({
          workspaceUuid: workspaceId,
          resourceId: viewId,
          queryParams: null,
        })
      )
    )
    new Set(proposals.map((p) => p.id)).forEach((blockId) =>
      queryClient.invalidateQueries(
        readBlock.queryKey(readBlock.queryIdentifiers(workspaceId, blockId))
      )
    )

    queryClient.invalidateQueries(
      globalQueryKeys.blocksByWorkspace(workspaceId)
    )
  }
}

export const handleUpdateMoment = (
  payload: UpdateMomentToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  const rows = batchRows(payload.result)

  // Always bust each moment's own cache, then refresh whatever view the user is
  // on (board / calendar week+month / moment) once for the whole batch.
  rows.forEach((row) => {
    if (row.id) {
      queryClient.invalidateQueries(globalQueryKeys.momentById(row.id))
    }
  })
  if (workspaceId) {
    invalidateMomentInContext(queryClient, workspaceId, entityContext)
  }
}

export const handleUpdateBoard = (
  payload: UpdateBoardToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined
) => {
  const proposal = findPendingProposalById(
    store.messages,
    payload.result.proposal_id
  ) as ProposedBoardUpdate | undefined

  if (workspaceId && proposal) {
    queryClient.invalidateQueries(boardListQueryKey(workspaceId, false))
    readBoard.invalidateCache(
      queryClient,
      boardsQueryKeys.boardIdentifiers(workspaceId, proposal.id)
    )
  }
}

export const handleUpdateBoardObject = (
  payload: UpdateBoardObjectToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  const rows = batchRows(payload.result)

  if (workspaceId) {
    // Cover both source and target collections by invalidating every
    // collection on the board.
    new Set(rows.map((row) => row.board_id)).forEach((boardId) => {
      if (boardId) {
        readBoardObjects.invalidateCache(
          queryClient,
          boardObjectsIdentifiers.board({ workspaceUuid: workspaceId, boardId })
        )
      }
    })
    rows.forEach((row) => {
      if (row.moment_id) {
        queryClient.invalidateQueries(globalQueryKeys.momentById(row.moment_id))
      }
    })
  }
}

export const handleSetCustomFieldValues = (
  payload: SetCustomFieldValuesToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  // Deploy skew: the web bundle and opal_ai ship separately, so an opal_ai from
  // before the batch tool still answers with its one row flat, carrying no
  // `results` array to read. Take it as the one row it describes — 'created'
  // and 'updated' were that shape's 'set'.
  const results =
    'results' in payload.result
      ? payload.result.results
      : [
          {
            ...payload.result,
            result:
              payload.result.result === 'not_set' ||
              payload.result.result === 'not_executed'
                ? payload.result.result
                : ('set' as const),
          },
        ]

  // One batch can set several values on the same block; refresh each block once.
  const changedBlockIds = new Set(
    results
      .filter((r) => r.result === 'set')
      .map((r) => r.block_id)
      .filter((id): id is string => !!id)
  )
  if (workspaceId) {
    changedBlockIds.forEach((blockId) =>
      queryClient.invalidateQueries(
        globalQueryKeys.blockById(workspaceId, blockId)
      )
    )
  }
}

export const handleSetMomentCustomFieldValues = (
  payload: SetMomentCustomFieldValuesToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  // Deploy skew, as in handleSetCustomFieldValues: an opal_ai without the batch
  // moment executor still answers with its one row flat.
  const results =
    'results' in payload.result
      ? payload.result.results
      : [
          {
            ...payload.result,
            result:
              payload.result.result === 'not_set' ||
              payload.result.result === 'not_executed'
                ? payload.result.result
                : ('set' as const),
          },
        ]

  // One batch can set several values on the same moment; refresh each once.
  const changedMomentIds = new Set(
    results
      .filter((r) => r.result === 'set')
      .map((r) => r.moment_id)
      .filter((id): id is string => !!id)
  )
  changedMomentIds.forEach((momentId) =>
    queryClient.invalidateQueries(globalQueryKeys.momentById(momentId))
  )
  // The view refresh is the same work whatever the batch touched, and the
  // calendar branch pokes the monolith iframe — so it runs once, not per moment.
  if (workspaceId && changedMomentIds.size > 0) {
    invalidateMomentInContext(queryClient, workspaceId, entityContext)
  }
}

// A content note reaches the open post through the monolith's push, as
// update_content's copy does; only moment notes need the moment queries
// refreshed.
export const handleSetNotes = (
  payload: SetNotesToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  const { results } = payload.result
  const changedMomentIds = new Set(
    results
      .filter((r) => r.result === 'set')
      .map((r) => r.moment_id)
      .filter((id): id is string => !!id)
  )
  changedMomentIds.forEach((momentId) =>
    queryClient.invalidateQueries(globalQueryKeys.momentById(momentId))
  )
  if (workspaceId && changedMomentIds.size > 0) {
    invalidateMomentInContext(queryClient, workspaceId, entityContext)
  }
}

export const handleCreatePlan = (
  payload: CreatePlanToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined,
  navigate: (to: string) => void,
  workspaceSlug: string | undefined
) => {
  if (workspaceId) {
    queryClient.invalidateQueries(
      globalQueryKeys.allPlansByWorkspace(workspaceId)
    )
    // The plans directory lists plans as browsable child-view *blocks*, so it
    // reads the blocks query rather than the plans query. Invalidate the
    // workspace blocks prefix so the new plan appears without a refresh.
    queryClient.invalidateQueries(
      globalQueryKeys.blocksByWorkspace(workspaceId)
    )
  }

  // Drop the user into the new plan so they watch Gem build it out. `create_plan`
  // now captures the plan's type, so we skip the `?created=1` setup modal — the
  // agent continues straight into swimlanes/blocks/moments/content in the same
  // flow instead of handing off to the modal.
  if (
    workspaceSlug &&
    payload.result.result === 'created' &&
    payload.result.id
  ) {
    navigate(`/moments/${workspaceSlug}/plans/${payload.result.id}/edit`)
  }
}

export const handleUpdatePlan = (
  payload: UpdatePlanToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  if (workspaceId && payload.result.id) {
    // A plan is a view; refresh the view itself, plus the directory — which
    // lists plans as browsable parent *blocks* (so it reads the blocks query,
    // not the plans query). Mirror create_plan's directory invalidations so a
    // rename from the directory shows without a refresh.
    queryClient.invalidateQueries(
      readPlan.queryKey({
        workspaceUuid: workspaceId,
        resourceId: payload.result.id,
        queryParams: null,
      })
    )
    queryClient.invalidateQueries(
      globalQueryKeys.allPlansByWorkspace(workspaceId)
    )
    queryClient.invalidateQueries(
      globalQueryKeys.blocksByWorkspace(workspaceId)
    )
  }
}

// Batch executor for all confirmed swimlanes. One call can set lanes up across
// several blocks' sub-plans, so every view it touched is refreshed, plus each
// parent block that just gained a sub-plan and the plan the user is on.
export const handleCreateSwimlanes = (
  payload: CreateSwimlanesToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  if (workspaceId) {
    invalidateViews(queryClient, workspaceId, affectedViewIds(payload.result))

    // Lanes created inside a block give that block its sub-plan, so the parent
    // block itself has to re-read (its Timeline tab replaces "No Plan Added
    // Yet"), and the plan showing it has to re-read its blocks. Mirrors
    // handleCreateBlocks.
    const parentBlockIds = new Set(
      payload.result.results
        .map(
          (result) =>
            (
              findPendingProposalById(store.messages, result.proposal_id) as
                | ProposedSwimlane
                | undefined
            )?.relationships.parent_block_id
        )
        .filter((id): id is string => Boolean(id))
    )
    parentBlockIds.forEach((parentBlockId) => {
      queryClient.invalidateQueries(
        readBlock.queryKey(
          readBlock.queryIdentifiers(workspaceId, parentBlockId)
        )
      )
    })

    if (parentBlockIds.size > 0) {
      invalidatePlanInContext(queryClient, workspaceId, entityContext)
      queryClient.invalidateQueries(
        globalQueryKeys.blocksByWorkspace(workspaceId)
      )
    }
  }
}

// The batch executor creates every confirmed smart swimlane on one plan view,
// so a single plan refresh surfaces the new lanes.
export const handleCreateSmartSwimlane = (
  payload: CreateSmartSwimlaneToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  if (workspaceId && payload.result.view_id) {
    queryClient.invalidateQueries(
      readPlan.queryKey({
        workspaceUuid: workspaceId,
        resourceId: payload.result.view_id,
        queryParams: null,
      })
    )
  }
}

export const handleUpdateCategories = (
  queryClient: QueryClient,
  workspaceId: string | undefined,
  entityContext: EntityContext
) => {
  // Renamed swimlanes live on the plan view; the executor echoes no view id,
  // so refresh the plan in context when there is one. One refresh covers the
  // whole batch.
  if (workspaceId) {
    invalidatePlanInContext(queryClient, workspaceId, entityContext)
  }
}

// Collections render inside the board view; refresh the board, its
// collections and every collection's objects so a new or changed section shows.
const invalidateBoardCollections = (
  queryClient: QueryClient,
  workspaceId: string,
  boardId: string
) => {
  readBoard.invalidateCache(
    queryClient,
    boardsQueryKeys.boardIdentifiers(workspaceId, boardId)
  )
  queryClient.invalidateQueries(
    boardsQueryKeys.boardCollections(workspaceId, boardId)
  )
  readBoardObjects.invalidateCache(
    queryClient,
    boardObjectsIdentifiers.board({ workspaceUuid: workspaceId, boardId })
  )
}

export const handleCreateCollections = (
  payload: CreateCollectionsToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  // The batch executor creates every confirmed collection on one board, so a
  // single board refresh covers them all.
  if (workspaceId && payload.result.board_id) {
    invalidateBoardCollections(
      queryClient,
      workspaceId,
      payload.result.board_id
    )
  }
}

export const handleCreateCustomFields = (
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  // A custom field belongs to the workspace, not to any one plan or board, so
  // there is no per-item id to scope the refresh with. The prefix key covers
  // every parameterization of the custom-fields read.
  if (workspaceId) {
    queryClient.invalidateQueries(globalQueryKeys.customFields(workspaceId))
  }
}

/**
 * An option change rewrites the field's whole option blob, so the same
 * workspace-wide custom-fields key `handleCreateCustomFields` invalidates is
 * what makes the new (or newly hidden) choice appear in every picker.
 */
export const handleUpdateFieldOptions = (
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  if (workspaceId) {
    queryClient.invalidateQueries(globalQueryKeys.customFields(workspaceId))
  }
}

// A type's configuration is read through two independent query namespaces —
// the admin screen's and the plans' type picker — and neither hangs off
// `globalQueryKeys`. Both root keys are invalidated so a change Gem makes shows
// up wherever the user happens to be looking.
const invalidateCategoryTypes = (queryClient: QueryClient) => {
  workspaceCategoryTypesQueryHelper.invalidateRootQuery(queryClient)
  planTypesQueryHelper.invalidateRootQuery(queryClient)
}

export const handleCreateCategoryTypes = (queryClient: QueryClient) => {
  invalidateCategoryTypes(queryClient)
}

export const handleUpdateTypeConfig = (queryClient: QueryClient) => {
  invalidateCategoryTypes(queryClient)
}

export const handleUpdateCollection = (
  payload: UpdateCollectionToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined
) => {
  const proposal = findPendingProposalById(
    store.messages,
    payload.result.proposal_id
  ) as ProposedCollectionUpdate | undefined

  if (workspaceId && proposal) {
    invalidateBoardCollections(
      queryClient,
      workspaceId,
      proposal.relationships.board_id
    )
  }
}

export const handleCreateKeyDates = (
  payload: CreateKeyDatesToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  if (workspaceId) {
    invalidateViews(queryClient, workspaceId, affectedViewIds(payload.result))
  }
}

export const handleUpdateKeyDates = (
  payload: UpdateKeyDatesToolCallResult,
  queryClient: QueryClient,
  workspaceId: string | undefined
) => {
  if (workspaceId) {
    invalidateViews(queryClient, workspaceId, affectedViewIds(payload.result))
  }
}

// A connection touches two blocks in different plans (the supporting block and
// the supported block), so refresh both and the workspace blocks list. The
// block/plan ids come from each created connection's stored proposal.
export const handleCreateConnections = (
  payload: CreateConnectionsToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined
) => {
  if (workspaceId) {
    payload.result.results
      .filter((r) => r.result === 'created')
      .forEach((r) => {
        const proposal = findPendingProposalById(
          store.messages,
          r.proposal_id
        ) as ProposedConnection | undefined

        if (proposal) {
          invalidateConnectedBlocks(
            queryClient,
            workspaceId,
            proposal.block_id,
            proposal.supports_block_id
          )
        }
      })
    queryClient.invalidateQueries(
      globalQueryKeys.blocksByWorkspace(workspaceId)
    )
  }
}

export const handleDisconnectBlocks = (
  payload: DisconnectBlocksToolCallResult,
  queryClient: QueryClient,
  store: ThreadState,
  workspaceId: string | undefined
) => {
  if (workspaceId) {
    const proposal = findPendingProposalById(
      store.messages,
      payload.result.proposal_id
    ) as ProposedConnectionRemove | undefined

    if (proposal) {
      invalidateConnectedBlocks(
        queryClient,
        workspaceId,
        proposal.block_id,
        proposal.supports_block_id
      )
    }
    queryClient.invalidateQueries(
      globalQueryKeys.blocksByWorkspace(workspaceId)
    )
  }
}

const invalidateConnectedBlocks = (
  queryClient: QueryClient,
  workspaceId: string,
  blockId: string,
  supportsBlockId: string
) => {
  ;[blockId, supportsBlockId].forEach((id) => {
    queryClient.invalidateQueries(
      readBlock.queryKey(readBlock.queryIdentifiers(workspaceId, id))
    )
  })
}
